import styles from './AutoGrowingInput.module.scss';

import cn from 'classnames';

import {
  InputHTMLAttributes,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'style'
> & {
  suffix?: ReactNode;
  small?: boolean;
  suffixGap?: number;
  suffixPosition?: 'before' | 'after';
  onWidthChange?: (arg: number) => void;
};

export const AutoGrowingInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      placeholder,
      suffix,
      className,
      small = false,
      suffixGap = 10,
      onWidthChange,
      suffixPosition = 'after',
      ...props
    },
    ref,
  ) => {
    const suffixRef = useRef<HTMLSpanElement>(null);
    const inputFakeValue = useRef<HTMLSpanElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleSuffixClick = () => {
      inputRef.current?.focus();
    };

    const calculateWidth = useCallback(() => {
      if (onWidthChange) {
        const suffixWidth =
          suffixRef.current?.getBoundingClientRect().width || 0;
        const inputFakeWidth =
          inputFakeValue.current?.getBoundingClientRect().width || 0;
        const totalWidth = suffixWidth + inputFakeWidth;
        onWidthChange(totalWidth);
      }
    }, [onWidthChange]);

    useEffect(() => {
      if (onWidthChange) {
        const suffixElement = suffixRef.current;
        const inputFakeElement = inputFakeValue.current;

        if (!suffixElement || !inputFakeElement) return;

        const resizeObserver = new ResizeObserver(() => {
          calculateWidth();
        });

        resizeObserver.observe(suffixElement);
        resizeObserver.observe(inputFakeElement);

        calculateWidth();

        return () => {
          resizeObserver.disconnect();
        };
      }
    }, [calculateWidth]);

    const [inputRightPadding, setInputRightPadding] = useState<number>(0);

    useLayoutEffect(() => {
      const suffixWidth = suffixRef.current?.offsetWidth;
      setInputRightPadding(suffix && suffixWidth ? suffixWidth + suffixGap : 0);
    }, [suffix]);

    return (
      <div className={cn(styles.inputWrapper, small && styles.small)}>
        {suffixPosition === 'before' && suffix && (
          <span
            ref={suffixRef}
            className={cn(styles.suffix, styles.before)}
            onClick={handleSuffixClick}
          >
            {suffix}
          </span>
        )}
        <input
          className={`${styles.input} ${className || ''}`}
          style={small ? { paddingRight: inputRightPadding } : {}}
          value={value}
          placeholder={placeholder}
          ref={(el) => {
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
            inputRef.current = el;
          }}
          {...props}
        />

        <div
          className={styles.inputFakeValueWrapper}
          style={{ gap: suffixGap }}
        >
          <span ref={inputFakeValue} className={styles.inputFakeValue}>
            {value || placeholder}
          </span>
          {suffixPosition === 'after' && suffix && (
            <span
              ref={suffixRef}
              className={styles.suffix}
              onClick={handleSuffixClick}
            >
              {suffix}
            </span>
          )}
        </div>
      </div>
    );
  },
);

AutoGrowingInput.displayName = 'AutoGrowingInput';
